<script setup lang="ts">
import { useModal } from "vue-final-modal";
import {
  pushDataLayerClickPromo,
  pushDataLayerClickHotelDetails,
  getEnvVar,
} from "@/utils";
import { formatDate } from "@/utils/dateUtils";
import IframeModal from "./../IframeModal.vue";
import type { Product } from "vacv-common/src/types/api/promotion/Api";
import { ProductExtended, PromosOutputParams } from "@/../types/promos-types";
import WasPriceItem from "@/components/product-listing/WasPriceItem.vue";
import { LocalePromo } from "@/../types/promos";
import {
  VacvTripAdvisor,
  VacvPromoBadge,
  isValidTripAdvisorRating,
  HotelRating,
} from "vacv-components";
import { computed, ComputedRef, ref, onMounted, Ref } from "vue";
import { useI18n } from "vue-i18n";
import { AcvTokenClaims } from "vacv-common";
import FlexpayMonthlyPrice from "./FlexpayMonthlyPrice.vue";

interface Props {
  position: number;
  promotion: ProductExtended;
  promosParams: PromosOutputParams;
  localisedPromo?: LocalePromo;
  upliftInited: Ref<boolean>;
}
const props = defineProps<Props>();

const selectHotelDetails = (
  e: { preventDefault: () => void },
  promotion: ProductExtended,
  elementName: string
) => {
  e.preventDefault();

  pushDataLayerClickHotelDetails(promotion, elementName);

  const { open, close } = useModal({
    defaultModelValue: false,
    component: IframeModal,
    attrs: {
      url: promotion.hotel_details_link,
      clickToClose: true,
      escToClose: true,
      onClose() {
        close();
      },
    },
  });
  open();
};

const isB2BUser = ref<boolean>(false);

onMounted(async () => {
  if (window.sso) {
    const tokenValue: AcvTokenClaims = await window.sso.getUserToken();
    isB2BUser.value = window.sso.isB2BUser(tokenValue);
  }
});

const selectPromo = (
  e: { preventDefault: () => void },
  promotion: Product,
  promosParams: PromosOutputParams
) => {
  e.preventDefault();
  // this callback is called after the DataLayer has saved the analytics events
  const openPromo = async () => {
    if (isB2BUser.value) {
      const punchOutUrl = getEnvVar(
        locale.value === "en"
          ? "VITE_B2B_PACKAGE_SEARCH_PUNCHOUT_URL_EN"
          : "VITE_B2B_PACKAGE_SEARCH_PUNCHOUT_URL_FR"
      );
      const sid = await window.sso.getSirevSid();
      const sirevUrl = punchOutUrl + `?sid=${sid}`;
      window.open(sirevUrl, "_blank");
    } else {
      window.location = promotion.action_url;
    }
  };

  pushDataLayerClickHotelDetails(promotion, "Select Button");

  pushDataLayerClickPromo(promotion, promosParams, openPromo);
};
const { locale } = useI18n();
const checkInDate = formatDate(
  props.promotion.departure_date as string,
  locale.value
);
const checkOutDate = formatDate(
  props.promotion.return_date as string,
  locale.value
);

const hotelRating: ComputedRef<HotelRating> = computed(() => {
  return {
    source: "TA",
    value: props.promotion.rating?.rating,
    reviews: props.promotion.rating?.reviews,
    ratingImageUrl: props.promotion.rating?.ratingImageUrl,
  };
});

defineExpose({ hotelRating });

const promoPrice = props.promotion?.price ?? 0;
const flexPayAmount = promoPrice * 100; // flexpay amount is in cents
</script>
<template>
  <div class="hotel-item-wrapper">
    <div class="hotel-item">
      <div class="item-gallery">
        <div
          class="carousel-single-img"
          :onClick="(e) => selectHotelDetails(e, promotion, 'Hotel Picture')"
        >
          <img :src="promotion.images?.[0].link_large" alt="" />
        </div>
      </div>

      <div class="item-content">
        <div class="item-content-left">
          <vacv-promo-badge
            v-if="localisedPromo"
            class="aeroplanpoints"
            :badge="localisedPromo.Badge"
            :modal-title="$t('plp.promobadge.termsAndConditions')"
            :modal-text="localisedPromo.Terms"
            origin="plpAeroplan"
          />
          <h2 class="hotel-name">{{ promotion.hotel_name }}</h2>
          <div class="rating-ta-wrapper">
            <span class="hotel-rating">
              <span class="stars">
                <span>{{ promotion.star_category }}</span>
                <i class="fa-solid fa-star" aria-hidden="true"></i>
                <span class="sr-only">{{
                  " " + $t("plp.hotelRating.stars")
                }}</span>
              </span>
            </span>
            <span class="hotel-city">{{ promotion.hotel_city }}</span>
          </div>
          <div class="dates">
            <div class="checkin">
              <span>{{ $t("plp.labels.check-in") }}</span>
              {{ checkInDate }}
            </div>
            <div class="checkout">
              <span>{{ $t("plp.labels.check-out") }}</span>
              {{ checkOutDate }}
            </div>
          </div>
          <div
            class="trip-advisor-rating-container"
            v-if="isValidTripAdvisorRating(hotelRating)"
          >
            <vacv-trip-advisor :rating="hotelRating" />
          </div>
          <div
            v-if="promotion.hotel_details_link"
            class="desktop-only hotel-details-link"
          >
            <button
              target="_blank"
              class="link linklook"
              :onClick="
                (e) =>
                  selectHotelDetails(e, promotion, 'See Hotel Details Link')
              "
            >
              {{ $t("plp.labels.hoteldetails") }}
            </button>
          </div>
        </div>

        <div class="item-content-right">
          <div class="price-left">
            <was-price-item :product="promotion" />
            <div class="total-wrapper">
              <div class="price-per">
                {{ $t("common.pricedetails.from") }}
                <span class="price">{{ promotion.price_formatted }}</span>
                <span class="per">
                  &nbsp;
                  {{ $t("common.pricedetails.peradult") }}
                </span>
              </div>
              <div class="price-taxes">
                {{ $t("common.pricedetails.taxesincl") }}
              </div>
              <flexpay-monthly-price
                :product="promotion"
                :price="flexPayAmount"
                priceType="hotel_option"
                :uplift-inited="upliftInited"
              />
            </div>
          </div>

          <div class="price-right">
            <div class="hotel-select">
              <button
                class="vacv-btn-legacy"
                :onClick="(e) => selectPromo(e, promotion, promosParams)"
              >
                {{ $t("common.buttons.select") }}
                <span class="sr-only">
                  &nbsp;{{ $t("plp.access.selectpackage") }}
                </span>
              </button>
            </div>
          </div>
        </div>

        <div
          v-if="promotion.hotel_details_link"
          class="mobile-only hotel-details-link"
          aria-hidden="true"
        >
          <button
            target="_blank"
            class="link linklook"
            :onClick="
              (e) => selectHotelDetails(e, promotion, 'See Hotel Details Link')
            "
          >
            {{ $t("plp.labels.hoteldetails") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
<style lang="scss">
@use "vacv-components/themes/acv-default/default-includes" as *;

.vacv-promo-badge__modal {
  .modal__close {
    &::before {
      margin-right: 0;
      font: var(--fa-font-light) !important;
      font-size: 2.4rem !important;
    }
  }
}
</style>
